import { useState } from 'react';
import { Disc, Download, Server } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { config } from '@/config';
import type { FirmwareImage } from '@/features/firmware';
import { classifyFirmwareFile, formatFileSize } from './firmwareArtifacts';
import { withAccount } from '@/lib/activeAccount';

interface Props {
  firmwareImages: FirmwareImage[] | undefined;
  /** Rendered above the downloads — the provisioning token to type at first boot. */
  tokenSlot?: React.ReactNode;
  /** Install options that need the token, shown before the downloads (Hyper-V). */
  installSlot?: React.ReactNode;
}

/**
 * OVA and ISO downloads for a gateway appliance.
 *
 * Shared by the standalone Downloads dialog (where the job is "get me the image
 * files", including older ISOs for upgrades) and by the image path of the Add
 * gateway flow (where it sits next to the token the installer will ask for).
 */
export function GatewayImageDownloads({ firmwareImages, tokenSlot, installSlot }: Props) {
  const [archFilter, setArchFilter] = useState('');
  const [showOlderIsos, setShowOlderIsos] = useState(false);
  const backendApi = config.backendApi;

  const downloadable = (firmwareImages ?? []).filter(
    (img) => classifyFirmwareFile(img.filename) !== null,
  );
  const isoOnly = downloadable.filter((img) => classifyFirmwareFile(img.filename) === 'iso');
  const isoArchitectures = [...new Set(isoOnly.map((img) => img.architecture))].sort();
  const activeArch = archFilter || (isoArchitectures.length > 0 ? isoArchitectures[0] : '');
  const isoImages = isoOnly
    .filter((img) => img.architecture === activeArch)
    // Newest first so the current image is featured; the rest go under the disclosure.
    .sort((a, b) =>
      b.version.localeCompare(a.version, undefined, { numeric: true, sensitivity: 'base' }),
    );
  const latestIso = isoImages[0];
  const olderIsos = isoImages.slice(1);
  // Each release carries its own OVA (amd64 only); offer the newest.
  const latestOva = downloadable
    .filter((img) => classifyFirmwareFile(img.filename) === 'ova')
    .sort((a, b) =>
      b.version.localeCompare(a.version, undefined, { numeric: true, sensitivity: 'base' }),
    )[0] as FirmwareImage | undefined;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Download a bootable image and run it in a hypervisor or on bare metal. Once it&apos;s
        running, add it with <span className="font-medium">Find it on this network</span>, or
        claim it on its own page at{' '}
        <code className="font-mono text-xs">http://&lt;its address&gt;:8080</code> with a
        provisioning token.
      </p>

      {tokenSlot}

      {installSlot}

      {isoArchitectures.length > 1 ? (
        <div className="inline-flex rounded-md border border-border-subtle overflow-hidden">
          {isoArchitectures.map((arch) => (
            <button
              key={arch}
              type="button"
              className={cn(
                'px-3 py-1.5 text-sm border-r border-border-subtle last:border-r-0',
                activeArch === arch ? 'bg-sev-5/15 text-sev-5' : 'hover:bg-accent/40',
              )}
              onClick={() => { setArchFilter(arch); }}
            >
              {arch}
            </button>
          ))}
        </div>
      ) : null}

      <div className="rounded-md border border-border-subtle p-4">
        <div className="flex items-start gap-3 mb-3">
          <Server className="w-8 h-8 text-sev-5 shrink-0" />
          <div>
            <h4 className="font-semibold">Virtual appliance (OVA)</h4>
            <p className="text-sm text-muted-foreground">
              The easiest way to get started. Import this OVA into VMware, VirtualBox, or
              any compatible hypervisor.
            </p>
          </div>
        </div>
        {latestOva ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-accent/20 px-3 py-2.5">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-semibold">{latestOva.version}</span>
                <span className="shrink-0 rounded bg-accent/50 px-1.5 py-0.5 text-xs text-muted-foreground">
                  {latestOva.architecture}
                </span>
              </div>
              <p
                className="mt-0.5 truncate text-xs text-muted-foreground"
                title={latestOva.filename}
              >
                {latestOva.filename}
                {latestOva.sizeBytes > 0 ? ` · ${formatFileSize(latestOva.sizeBytes)}` : ''}
              </p>
            </div>
            <Button asChild size="sm" className="shrink-0 gap-1.5">
              <a
                href={withAccount(`${backendApi}/api/firmware-images/${latestOva.id}/download`)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download className="w-4 h-4" />
                Download OVA
              </a>
            </Button>
          </div>
        ) : (
          <p className="text-sm italic text-muted-foreground">No OVA available yet.</p>
        )}
      </div>

      <div className="rounded-md border border-border-subtle p-4">
        <div className="flex items-start gap-3 mb-3">
          <Disc className="w-8 h-8 text-sev-5 shrink-0" />
          <div>
            <h4 className="font-semibold">ISO image</h4>
            <p className="text-sm text-muted-foreground">
              For bare-metal appliances or manual VM installation.
            </p>
          </div>
        </div>
        {isoImages.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-accent/20 px-3 py-2.5">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold">{latestIso.version}</span>
                  <span className="shrink-0 rounded bg-sev-5/15 px-1.5 py-0.5 text-xs font-medium text-sev-5">
                    Latest
                  </span>
                  <span className="shrink-0 rounded bg-accent/50 px-1.5 py-0.5 text-xs text-muted-foreground">
                    {latestIso.architecture}
                  </span>
                </div>
                <p
                  className="mt-0.5 truncate text-xs text-muted-foreground"
                  title={latestIso.filename}
                >
                  {latestIso.filename}
                  {latestIso.sizeBytes > 0 ? ` · ${formatFileSize(latestIso.sizeBytes)}` : ''}
                </p>
              </div>
              <Button asChild size="sm" className="shrink-0 gap-1.5">
                <a
                  href={withAccount(`${backendApi}/api/firmware-images/${latestIso.id}/download`)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
              </Button>
            </div>

            {olderIsos.length > 0 ? (
              <Collapsible open={showOlderIsos} onOpenChange={setShowOlderIsos}>
                <CollapsibleTrigger className="flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground">
                  <ChevronRight
                    className={cn('h-4 w-4 transition-transform', showOlderIsos && 'rotate-90')}
                  />
                  {olderIsos.length} older version{olderIsos.length > 1 ? 's' : ''}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <ul className="mt-2 rounded-md border border-border-subtle">
                    {olderIsos.map((image) => (
                      <li
                        key={image.id}
                        className="flex items-center justify-between gap-3 border-b border-border-subtle px-3 py-2 last:border-b-0"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm">{image.version}</span>
                            <span className="shrink-0 rounded bg-accent/50 px-1.5 py-0.5 text-xs text-muted-foreground">
                              {image.architecture}
                            </span>
                          </div>
                          <p
                            className="mt-0.5 truncate text-xs text-muted-foreground"
                            title={image.filename}
                          >
                            {image.filename}
                            {image.sizeBytes > 0 ? ` · ${formatFileSize(image.sizeBytes)}` : ''}
                          </p>
                        </div>
                        <Button asChild size="sm" variant="outline" className="shrink-0 gap-1.5">
                          <a
                            href={withAccount(`${backendApi}/api/firmware-images/${image.id}/download`)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </a>
                        </Button>
                      </li>
                    ))}
                  </ul>
                </CollapsibleContent>
              </Collapsible>
            ) : null}
          </div>
        ) : (
          <p className="text-sm italic text-muted-foreground">
            No ISO images available{activeArch ? ` for ${activeArch}` : ''}.
          </p>
        )}
      </div>
    </div>
  );
}
